import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback } from "react";

import {
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_READY,
  APPOINTMENT_STATUS_UNCONFIRMED
} from "constants/appointments";
import { FIND_NEXT_AVAILABLE_CHECKOUT_FEATURE } from "constants/feature-flags";
import {
  getCheckoutAppointmentTypesParams,
  useGetAppointmentTypesQuery
} from "services/appointment-types";
import { useLazyGetAppointmentsQuery } from "services/appointments";
import { AppointmentType } from "services/types/appointment-types.type";
import {
  Appointment,
  PatientAppointmentsFilters
} from "services/types/appointments.type";

const SCHEDULED_FOLLOW_UP_STATUSES = [
  APPOINTMENT_STATUS_DRAFT,
  APPOINTMENT_STATUS_UNCONFIRMED,
  APPOINTMENT_STATUS_CONFIRMED,
  APPOINTMENT_STATUS_ARRIVED,
  APPOINTMENT_STATUS_READY
];

const FOLLOW_UP_APPOINTMENT_QUERY = "{id,type{id,name},status,start_moment}";

type AppointmentForPrompt = Partial<Appointment>;

export default function useScheduleNextAppointmentPrompt() {
  const flags = useFlags();
  // Same params as the Schedule Next modal and the Current Visit drawer block
  // build, so all three checkout surfaces share one RTK Query cache entry.
  const { data: appointmentTypes = [] } = useGetAppointmentTypesQuery(
    getCheckoutAppointmentTypesParams(
      Boolean(flags[FIND_NEXT_AVAILABLE_CHECKOUT_FEATURE])
    )
  );
  const [getAppointments] = useLazyGetAppointmentsQuery();

  const getFullAppointmentType = useCallback(
    (appointment: AppointmentForPrompt): AppointmentType | null => {
      const appointmentType = appointment.appointmentType;
      if (!appointmentType?.id) return null;

      return (
        appointmentTypes.find((type) => type.id === appointmentType.id) ||
        (appointmentType as AppointmentType)
      );
    },
    [appointmentTypes]
  );

  const hasExistingScheduledFollowUp = useCallback(
    async (
      appointment: AppointmentForPrompt,
      appointmentType: AppointmentType
    ): Promise<boolean> => {
      const patientId = appointment.patient?.id;
      const nextAppointmentTypeId = appointmentType.next_appointment_type?.id;

      if (!patientId || !nextAppointmentTypeId) return false;

      const afterMoment = appointment.start_moment || appointment.startDate;
      const queryParams: Partial<PatientAppointmentsFilters> = {
        patientId,
        type: String(nextAppointmentTypeId),
        status: SCHEDULED_FOLLOW_UP_STATUSES,
        ordering: "start_moment",
        limit: 5,
        query: FOLLOW_UP_APPOINTMENT_QUERY,
        ...(afterMoment ? { after_moment: afterMoment } : {})
      };

      const appointments = await getAppointments(queryParams).unwrap();

      return appointments.some(
        (followUpAppointment) =>
          Number(followUpAppointment.id) !== Number(appointment.id)
      );
    },
    [getAppointments]
  );

  const getPromptAppointment = useCallback(
    async (
      appointment: AppointmentForPrompt
    ): Promise<AppointmentForPrompt | null> => {
      const fullAppointmentType = getFullAppointmentType(appointment);

      if (!fullAppointmentType?.prompt_next_on_complete) return null;

      let hasExistingFollowUp = false;
      try {
        hasExistingFollowUp = await hasExistingScheduledFollowUp(
          appointment,
          fullAppointmentType
        );
      } catch {
        hasExistingFollowUp = false;
      }

      if (hasExistingFollowUp) return null;

      return {
        ...appointment,
        appointmentType: fullAppointmentType
      };
    },
    [getFullAppointmentType, hasExistingScheduledFollowUp]
  );

  const getSuggestedAppointmentType = useCallback(
    (appointment: AppointmentForPrompt | null): AppointmentType | null => {
      const nextType = appointment?.appointmentType?.next_appointment_type;
      if (!nextType) return null;

      return (
        appointmentTypes.find((type) => type.id === nextType.id) ||
        (nextType as AppointmentType)
      );
    },
    [appointmentTypes]
  );

  return {
    appointmentTypes,
    getPromptAppointment,
    getSuggestedAppointmentType
  };
}
